import React, { CSSProperties, Fragment, PureComponent, ReactNode } from 'react';
import { Select, Tag } from 'antd';
const { Option } = Select;
import { BaseSelectRef } from 'rc-select/lib/index';

import onContainerClick from '../../../../../pages/prescriptionPad/onClickContainer';
import { EkaFocosableElement } from '../../../../common/types/EkaFocusable';

export interface OptionTypeBaseObj {
    id: string;
    [key: string]: any;
}

export type OptionTypeBase = OptionTypeBaseObj | string;

export type ValueType<T extends OptionTypeBase, IsMulti extends boolean> = IsMulti extends true
    ? ReadonlyArray<T>
    : T;

interface Props<OptionType extends OptionTypeBase, IsMulti extends boolean = false> {
    isMulti?: IsMulti | undefined;
    className?: string;
    filterOption?: boolean;
    value?: ValueType<OptionType, IsMulti>;
    showSearch?: boolean;
    open?: boolean;
    showArrow?: boolean;
    defaultOpen?: boolean;
    style?: CSSProperties;
    autoFocus?: boolean;
    allowClear: boolean;
    placeholder?: string;
    optionFilterProp?: string;
    onFocus?: (e?: React.FocusEvent<HTMLElement>) => void;
    onBlur?: (e?: React.FocusEvent<HTMLElement>) => void;
    options: ReadonlyArray<OptionType>;
    getValue: (option: OptionType) => string;
    setRef?: (ref: EkaFocosableElement) => void;
    getLabelToCompare: (option: OptionType) => any;
    notFoundContent?: ReactNode;
    containerIdToAttachDropdown?: string;
    setValue?: (option?: ValueType<OptionType, IsMulti>) => void;
    disabled?: boolean;
    customDropdownWidth?: number;
    tabIndex?: number;
    createOption?: (text: string) => OptionType;
}

export type AntSelectProps<T extends OptionTypeBase, IsMulti extends boolean = false> = Props<
    T,
    IsMulti
>;

type State = {
    menuIsOpen?: boolean;
    search?: string;
};

class AntSelect<T extends OptionTypeBase, IsMulti extends boolean = false> extends PureComponent<
    Props<T, IsMulti>,
    State
> {
    state: State = { menuIsOpen: this.props.defaultOpen };
    select: BaseSelectRef | undefined = undefined;
    clearTriggered = false;

    onFocus = (e: React.FocusEvent<HTMLElement>): void => {
        this.props.onFocus && this.props.onFocus(e);
        this.setState({
            menuIsOpen: true,
        });
    };

    onBlur = (e: React.FocusEvent<HTMLElement>): void => {
        onContainerClick();
        this.props.onBlur && this.props.onBlur(e);
        this.setState({
            menuIsOpen: false,
        });
    };

    onSearch = (value: string): void => {
        this.setState({
            search: value,
        });
    };

    onSelect = (
        selectedValue?:
            | string
            | number
            | { label: ReactNode; value: string | number; key?: React.Key }
            | string[],
        option?: { disabled?: boolean; [name: string]: any },
    ): void => {
        const { isMulti, options, getValue, setValue, value, createOption } = this.props;
        this.clearTriggered = false;

        if (!selectedValue) {
            return;
        }

        let t: T | undefined = options.find((o) => getValue(o) === selectedValue);
        if (!t) {
            if (!createOption) {
                return;
            }

            t = selectedValue as T;
        }

        if (setValue) {
            if (isMulti) {
                /**
                 * TS is acking noob here
                 * given that
                 */
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                setValue([...(value || []), t]);
            } else {
                /**
                 * TS is acking noob here
                 * given that
                 */
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                setValue(t);
            }
        }

        !isMulti && this.select?.blur();

        !isMulti &&
            this.setState({
                menuIsOpen: false,
            });
    };

    onDeselect = (
        selectedValue?:
            | string
            | number
            | { label: ReactNode; value: string | number; key?: React.Key }
            | string[],
        option?: { disabled?: boolean; [name: string]: any },
    ): void => {
        const { isMulti, options, getValue, setValue, value } = this.props;

        if (!selectedValue || this.clearTriggered) {
            return;
        }

        const t: T | undefined = options.find((o) => getValue(o) === selectedValue);

        if (!t) {
            return;
        }

        if (setValue) {
            if (isMulti) {
                const newValue = ((value as []) || []).filter(
                    (item) => getValue(item) !== getValue(t),
                );

                /**
                 * TS is acking noob here
                 * given that
                 */
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                setValue(newValue);
            } else {
                setValue(undefined);
            }
        }
    };

    onClear = (): void => {
        this.clearTriggered = true;
        const { setValue } = this.props;

        setValue && setValue();
    };

    ref = (ref: BaseSelectRef): void => {
        const { setRef } = this.props;
        setRef && ref && setRef(ref);
        this.select = ref;
    };

    onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>): void => {
        if (e.key === 'Escape') {
            this.select?.blur();
        }
        this.clearTriggered = false;
    };

    tagRender = (props: {
        label: React.ReactNode;
        value: any;
        disabled: boolean;
        onClose: (event?: React.MouseEvent<HTMLElement, MouseEvent>) => void;
        closable: boolean;
    }): JSX.Element => {
        const { value, onClose } = props;
        const { options, getValue, getLabelToCompare } = this.props;

        const onPreventMouseDown = (event: React.MouseEvent<HTMLElement>) => {
            event.preventDefault();
            event.stopPropagation();
        };

        const op = options.find((item) => getValue(item) === value);
        if (!op) {
            return <Fragment></Fragment>;
        }

        return (
            <Tag
                key={value + '-tag'}
                className="bg-status-infoBG text-darwin-primary-500"
                onMouseDown={onPreventMouseDown}
                closable={true}
                onClose={onClose}
            >
                {getLabelToCompare(op)}
            </Tag>
        );
    };

    render(): ReactNode {
        const {
            className,
            options,
            getLabelToCompare,
            filterOption,
            showArrow,
            value,
            defaultOpen,
            style,
            autoFocus,
            allowClear,
            placeholder,
            optionFilterProp,
            getValue,
            isMulti,
            showSearch,
            notFoundContent,
            containerIdToAttachDropdown,
            disabled,
            customDropdownWidth,
            tabIndex,
            createOption,
        } = this.props;
        const { menuIsOpen, search } = this.state;

        const v = Array.isArray(value)
            ? value.map(getValue) || []
            : value
            ? /**
               * We have already added check if value is array. still TS gives error
               */
              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              // @ts-ignore
              getValue(value)
            : undefined;

        let availableOptions = Array.isArray(value)
            ? options.filter((op) => !value?.find((sop) => getValue(sop) === getValue(op)))
            : options;

        if (
            createOption &&
            search?.length &&
            !availableOptions.find((t) => t.toLowerCase() === search.toLowerCase())
        ) {
            availableOptions = [...availableOptions, createOption(search)];
        }

        return (
            <Select
                mode={isMulti ? 'multiple' : undefined}
                showSearch={showSearch}
                ref={this.ref}
                dropdownClassName="font-lato"
                className={className}
                open={menuIsOpen}
                onFocus={this.onFocus}
                onBlur={this.onBlur}
                onSelect={this.onSelect}
                onSearch={this.onSearch}
                onKeyDown={this.onKeyDown}
                filterOption={filterOption}
                value={v}
                showArrow={showArrow}
                defaultOpen={defaultOpen}
                style={style}
                autoFocus={autoFocus}
                allowClear={allowClear}
                placeholder={placeholder}
                optionFilterProp={optionFilterProp}
                onClear={this.onClear}
                onDeselect={this.onDeselect}
                notFoundContent={notFoundContent}
                tagRender={this.tagRender}
                disabled={disabled}
                dropdownMatchSelectWidth={customDropdownWidth}
                getPopupContainer={() =>
                    containerIdToAttachDropdown
                        ? (document.getElementById(containerIdToAttachDropdown) as HTMLElement)
                        : document.body
                }
                tabIndex={tabIndex}
            >
                {(availableOptions || []).map((l, index) => (
                    <Option
                        // @ts-expect-error ts version upgrade
                        {...l}
                        key={`${typeof l === 'object' && 'id' in l ? l.id : l.toString()}-${index}`}
                        value={typeof l === 'object' && 'id' in l ? getValue(l) : l.toString()}
                    >
                        {getLabelToCompare(l)}
                    </Option>
                ))}
            </Select>
        );
    }
}

export default AntSelect;
